import type {
  PaginatedResponse,
  PaginationOptions,
  QueryOptions,
  SortOrder,
} from '@/types/api.types.ts'
import {
  HTTP_GET_FILTERS,
  HTTP_GET_FILTER_DEFAULT_VALUES,
} from '@/constants/apiConstants.ts'
import { ROUTES } from '@/constants/routeConstants'

import {
  clearAuthSession,
  getAuthToken,
  isTokenExpired,
} from '@/utils/apiAuthUtils.ts'

type MutationMethod = 'POST' | 'PATCH' | 'PUT' | 'DELETE'

export class HttpError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'HttpError'
  }
}

// Jetée quand secureRequest déclenche déjà une redirection en dur
// (window.location.href). Un type dédié (plutôt qu'une Error générique)
// permet au root route de la reconnaître et de n'afficher aucun fallback
// pendant que le navigateur termine la navigation — voir __root.tsx.
export class SilentRedirectError extends Error {
  constructor() {
    super('Redirecting')
    this.name = 'SilentRedirectError'
  }
}

const extractErrorMessage = async (
  response: Response,
  fallback: string,
): Promise<string> => {
  try {
    const data = await response.json()

    if (typeof data?.message === 'string' && data.message.trim()) {
      return data.message
    }
    if (typeof data?.detail === 'string' && data.detail.trim()) {
      return data.detail
    }
    if (typeof data?.error === 'string' && data.error.trim()) {
      return data.error
    }
  } catch {
    // Corps vide ou non-JSON (proxy, 5xx HTML...) → on garde le fallback générique.
  }

  return fallback
}

const buildRequestInit = (init: RequestInit = {}): RequestInit => {
  const token = getAuthToken()
  const headers = new Headers(init.headers)

  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  return {
    ...init,
    headers,
  }
}

const secureRequest = async (
  url: URL,
  init?: RequestInit,
): Promise<Response> => {
  // Before making the request, verify token validity
  if (isTokenExpired()) {
    // Clear both session object and any raw access_token key, then redirect
    clearAuthSession()
    // Redirect to login (will stop current navigation)
    window.location.href = ROUTES.ROOT
    throw new SilentRedirectError()
  }

  const response = await fetch(url.toString(), buildRequestInit(init))

  // If server says unauthorized, treat it as expired/invalid token
  if (response.status === 401) {
    clearAuthSession()
    window.location.href = ROUTES.ROOT
    throw new SilentRedirectError()
  }

  // Authentifié mais pas autorisé sur cette ressource (mauvais compte/rôle,
  // ou désync entre le rôle local et le backend) : on ne touche pas à la
  // session (le token reste valide), on redirige juste vers l'accueil au
  // lieu de laisser l'erreur remonter jusqu'à l'error boundary générique.
  if (response.status === 403) {
    window.location.href = ROUTES.ROOT
    throw new SilentRedirectError()
  }

  return response
}

export const secureFetch = async <T>(
  url: URL,
  resourceLabel: string,
  init?: RequestInit,
): Promise<T> => {
  const response = await secureRequest(url, init)

  if (!response.ok) {
    const fallback = `Failed to fetch ${resourceLabel}: ${response.statusText} ${response.status}`
    throw new HttpError(
      response.status,
      await extractErrorMessage(response, fallback),
    )
  }

  return response.json()
}

const secureMutation = async <TResponse, TBody>(
  url: URL,
  resourceLabel: string,
  method: MutationMethod,
  body?: TBody,
): Promise<TResponse> => {
  const response = await secureRequest(url, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (!response.ok) {
    const fallback = `Failed to ${method.toLowerCase()} ${resourceLabel}: ${response.statusText} ${response.status}`
    throw new HttpError(
      response.status,
      await extractErrorMessage(response, fallback),
    )
  }

  if (response.status === 204) {
    return undefined as TResponse
  }

  return response.json()
}

const doPaginatedFetch = async <T>(url: URL, resourceLabel: string) => {
  return secureFetch<PaginatedResponse<T>>(url, resourceLabel)
}

export const simpleResource =
  <TItem>(
    buildPath: () => string,
    resourceLabel: string,
    params?: Record<string, string>,
  ) =>
  async () => {
    const url = new URL(buildPath(), window.location.origin)
    if (params) {
      Object.entries(params).forEach(([key, value]) =>
        url.searchParams.set(key, value),
      )
    }

    return secureFetch<TItem>(url, resourceLabel)
  }

export interface ConsultantFilters {
  rtype?: string
  typeContract?: 'cdi' | 'freelance'
  desiredDailyRateMin?: number
  desiredDailyRateMax?: number
  desiredSalaryMin?: number
  desiredSalaryMax?: number
  cleverscoreMin?: number
  experiences?: Array<string>
  skills?: Array<number>
  city?: string
  remoteOnly?: boolean
  favoritesOnly?: boolean
  availabilities?: Array<number>
}

export interface SearchResourcesBody {
  offset: number
  limit: number
  rtype?: string
  query?: string
  sort?: string
  type_contract?: 'cdi' | 'freelance'
  desired_daily_rate_min?: number
  desired_daily_rate_max?: number
  desired_salary_min?: number
  desired_salary_max?: number
  cleverscore_min?: number
  town?: string
  skills?: Array<number>
  availabilities?: Array<number>
  experiences?: Array<string>
  remote_only?: boolean
  favorites_only?: boolean
}

export const resourceWithPagination =
  <TItem>(buildPath: () => string, resourceLabel: string) =>
  async (
    {
      offset = Number(HTTP_GET_FILTER_DEFAULT_VALUES[HTTP_GET_FILTERS.OFFSET]),
      limit = Number(HTTP_GET_FILTER_DEFAULT_VALUES[HTTP_GET_FILTERS.LIMIT]),
    }: PaginationOptions = {},
    query?: string,
    sort?: string,
    filters?: Pick<ConsultantFilters, 'rtype'>,
  ) => {
    const url = new URL(buildPath(), window.location.origin)
    url.searchParams.set(HTTP_GET_FILTERS.OFFSET, offset.toString())
    url.searchParams.set(HTTP_GET_FILTERS.LIMIT, limit.toString())

    if (query) url.searchParams.set('query', query)
    if (sort) url.searchParams.set('sort', sort)
    if (filters?.rtype) url.searchParams.set('rtype', filters.rtype)

    return doPaginatedFetch<TItem>(url, resourceLabel)
  }

export const resourceWithPaginationAndSort =
  <TItem>(buildPath: () => string, resourceLabel: string) =>
  async ({
    offset = Number(HTTP_GET_FILTER_DEFAULT_VALUES[HTTP_GET_FILTERS.OFFSET]),
    limit = Number(HTTP_GET_FILTER_DEFAULT_VALUES[HTTP_GET_FILTERS.LIMIT]),
    sortBy,
    sortOrder = HTTP_GET_FILTER_DEFAULT_VALUES[
      HTTP_GET_FILTERS.SORT_ORDER
    ] as SortOrder,
    filters,
    search,
  }: QueryOptions = {}) => {
    const url = new URL(buildPath(), window.location.origin)
    url.searchParams.set(HTTP_GET_FILTERS.OFFSET, offset.toString())
    url.searchParams.set(HTTP_GET_FILTERS.LIMIT, limit.toString())

    if (sortBy) {
      url.searchParams.set(HTTP_GET_FILTERS.SORT_BY, sortBy)
      url.searchParams.set(HTTP_GET_FILTERS.SORT_ORDER, sortOrder)
    }

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) url.searchParams.set(key, value)
      })
    }

    if (search && search.trim() !== '') {
      url.searchParams.set(HTTP_GET_FILTERS.SEARCH, search.trim())
    }

    return doPaginatedFetch<TItem>(url, resourceLabel)
  }

export const resourceSearchWithPagination =
  <TItem>(buildPath: () => string, resourceLabel: string) =>
  async (
    {
      offset = Number(HTTP_GET_FILTER_DEFAULT_VALUES[HTTP_GET_FILTERS.OFFSET]),
      limit = Number(HTTP_GET_FILTER_DEFAULT_VALUES[HTTP_GET_FILTERS.LIMIT]),
    }: PaginationOptions = {},
    query?: string,
    sort?: string,
    filters?: ConsultantFilters,
  ) => {
    const url = new URL(buildPath(), window.location.origin)

    const body: SearchResourcesBody = {
      offset,
      limit,
    }

    if (query) body.query = query
    if (sort) body.sort = sort

    if (filters) {
      if (filters.rtype) body.rtype = filters.rtype
      if (filters.typeContract) body.type_contract = filters.typeContract
      if (filters.desiredDailyRateMin)
        body.desired_daily_rate_min = filters.desiredDailyRateMin
      if (filters.desiredDailyRateMax)
        body.desired_daily_rate_max = filters.desiredDailyRateMax
      if (filters.desiredSalaryMin)
        body.desired_salary_min = filters.desiredSalaryMin
      if (filters.desiredSalaryMax)
        body.desired_salary_max = filters.desiredSalaryMax
      if (filters.cleverscoreMin) body.cleverscore_min = filters.cleverscoreMin
      if (filters.city) body.town = filters.city
      if (filters.remoteOnly) body.remote_only = filters.remoteOnly
      if (filters.favoritesOnly) body.favorites_only = filters.favoritesOnly
      if (filters.experiences && filters.experiences.length > 0)
        body.experiences = filters.experiences
      if (filters.skills && filters.skills.length > 0)
        body.skills = filters.skills
      if (filters.availabilities && filters.availabilities.length > 0)
        body.availabilities = filters.availabilities
    }

    return secureFetch<PaginatedResponse<TItem>>(url, resourceLabel, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })
  }

export const resourceMutationWithBody =
  <TResponse, TBody>(
    buildPath: () => string,
    resourceLabel: string,
    method: MutationMethod,
  ) =>
  async (body?: TBody): Promise<TResponse> => {
    const url = new URL(buildPath(), window.location.origin)

    return secureMutation<TResponse, TBody>(url, resourceLabel, method, body)
  }

export const resourceWithSecureFetch =
  <TItem, TId>(buildPath: (id: TId) => string, resourceLabel: string) =>
  async (id: TId) => {
    const url = new URL(buildPath(id), window.location.origin)

    return secureFetch<TItem>(url, resourceLabel)
  }

export const resourceWithSecureMutation =
  <TId>(
    buildPath: (id: TId) => string,
    method: 'POST' | 'DELETE',
    resourceLabel: string,
  ) =>
  async (id: TId): Promise<void> => {
    const url = new URL(buildPath(id), window.location.origin)
    const response = await secureRequest(url, { method })

    if (!response.ok) {
      const fallback = `Failed to update ${resourceLabel}: ${response.statusText} ${response.status}`
      throw new HttpError(
        response.status,
        await extractErrorMessage(response, fallback),
      )
    }
  }

export const subresourceWithPagination =
  <TItem, TId>(buildPath: (id: TId) => string, resourceLabel: string) =>
  async (
    id: TId,
    {
      offset = Number(HTTP_GET_FILTER_DEFAULT_VALUES[HTTP_GET_FILTERS.OFFSET]),
      limit = Number(HTTP_GET_FILTER_DEFAULT_VALUES[HTTP_GET_FILTERS.LIMIT]),
    }: PaginationOptions = {},
  ) => {
    const url = new URL(buildPath(id), window.location.origin)

    url.searchParams.set(HTTP_GET_FILTERS.OFFSET, offset.toString())
    url.searchParams.set(HTTP_GET_FILTERS.LIMIT, limit.toString())

    return doPaginatedFetch<TItem>(url, resourceLabel)
  }
